import { useState, useEffect } from "react";
import { Table, Input, Tag, notification, Drawer } from "antd";
import { Button, Icon, Loading } from "~/components";
import { saleApi } from "~/api";
import { handleRequest, apiError, renderDateTime, strToTime } from "~/utils";
import SaleCallSourceEventForm from "./SaleCallSourceEventForm";

function SaleCallSourceDetail({ item }) {

	const [contacts, setContacts]       = useState([]);
	const [pagination, setPagination]   = useState({ page: 1, limit: 20, total: 0 });
	const [loading, setLoading]         = useState(false);
	const [keyword, setKeyword]         = useState('');
	const [contactEdit, setContactEdit] = useState(null);
	const [callNote, setCallNote]       = useState('');
	// panel: null | 'call' | 'event'
	const [activePanel, setActivePanel] = useState(null);

	const openCallPanel = (record) => {
		setContactEdit(record);
		setCallNote(record.note || '');
		setActivePanel('call');
	};

	const openEventPanel = (record) => {
		setContactEdit(record);
		setActivePanel('event');
	};

	const closePanel = () => {
		setActivePanel(null);
		setCallNote('');
	};

	const loadContacts = async (page = 1, kw = keyword) => {
		if (!item?.id) return;
		setLoading(true);
		let [error, response] = await handleRequest(
			saleApi.callSourceDetail(item.id, { page, limit: 20, keyword: kw })
		);
		let message = apiError('Tải danh sách thất bại', error, response);
		if (!message) {
			setContacts(response.contacts || []);
			setPagination(response.pagination || { page: 1, limit: 20, total: 0 });
		}
		setLoading(false);
	};

	useEffect(() => {
		if (item?.id) loadContacts(1, '');
	}, [item]);

	const handleSearch = (val) => {
		setKeyword(val);
		loadContacts(1, val);
	};

	// Xác nhận đã gọi
	const handleConfirmCall = async () => {
		if (!contactEdit?.id) return;
		let [error, response] = await handleRequest(
			saleApi.callSourceContactCalled(contactEdit.id, { note: callNote })
		);
		let message = apiError('Xác nhận gọi điện thất bại', error, response);
		if (!message) {
			notification.success({ message: 'Thành công', description: 'Đã xác nhận gọi điện' });
			setContacts(prev => prev.map(c =>
				c.id === contactEdit.id ? { ...c, ...response.data } : c
			));
			closePanel();
		}
	};

	// Tạo cuộc hẹn
	const handleCreateEvent = async (data, contact) => {
		data.day = strToTime(data.day);
		let [error, response] = await handleRequest(saleApi.eventAdd(data));
		let message = apiError('Tạo lịch hẹn thất bại', error, response);
		if (!message) {
			if (!response?.data?.id) {
				notification.error({ message: 'Thất bại', description: 'Chưa tạo được lịch hẹn' });
			} else {
				notification.success({ message: 'Thành công', description: `Đã tạo lịch hẹn cho ${contact?.name}` });
				closePanel();
			}
		}
	};

	const columns = [
		{
			title: 'STT',
			key: 'stt',
			width: 50,
			render: (_, __, i) => (pagination.page - 1) * pagination.limit + i + 1,
		},
		{ title: 'Tên khách/công ty', dataIndex: 'name',    key: 'name' },
		{ title: 'Số điện thoại',     dataIndex: 'phone',   key: 'phone',   width: 135 },
		{ title: 'Địa chỉ',           dataIndex: 'address', key: 'address' },
		{ title: 'Ghi chú',           dataIndex: 'note',    key: 'note' },
		{
			title: 'Trạng thái',
			dataIndex: 'status',
			key: 'status',
			width: 110,
			render: (status) => status == 1
				? <Tag color="green">Đã điện</Tag>
				: <Tag color="default">Chưa gọi</Tag>,
		},
		{
			title: 'Thời gian gọi',
			dataIndex: 'callAt',
			key: 'callAt',
			width: 140,
			render: (callAt) => callAt > 0
				? renderDateTime(callAt)
				: <span style={{ color: '#bbb' }}>—</span>,
		},
		{
			title: '',
			key: 'action',
			width: 115,
			render: (_, record) => (
				<div className="d-flex gap">
					<Button
						small outline
						leftIcon={Icon.headphones}
						style={{ color: record.status == 1 ? '#52c41a' : undefined }}
						onClick={() => openCallPanel(record)}
						title="Xác nhận gọi điện"
					/>
					<Button
						small blue outline
						leftIcon={Icon.calendar}
						onClick={() => openEventPanel(record)}
						title="Tạo cuộc hẹn"
					/>
				</div>
			),
		},
	];

	return (
		<div>
			{loading && <Loading />}

			<Input.Search
				placeholder="Tìm theo tên hoặc số điện thoại..."
				onSearch={handleSearch}
				allowClear
				style={{ marginBottom: 12, maxWidth: 340 }}
			/>

			<Table
				columns={columns}
				dataSource={contacts}
				rowKey="id"
				size="small"
				scroll={{ x: 900 }}
				rowClassName={(record) => record.status == 1 ? 'ant-table-row-called' : ''}
				pagination={{
					current: pagination.page,
					pageSize: pagination.limit,
					total: pagination.total,
					onChange: (page) => loadContacts(page),
					showTotal: (t) => `Tổng ${t} liên hệ`,
				}}
			/>

			{/* Drawer: Xác nhận gọi điện */}
			<Drawer
				title="📞 Xác nhận gọi điện"
				open={activePanel === 'call'}
				onClose={closePanel}
				width={400}
				destroyOnClose
			>
				{contactEdit && (
					<div>
						<div style={{
							background: '#f5f5f5', borderRadius: 6,
							padding: '10px 14px', marginBottom: 14,
						}}>
							<p style={{ margin: '3px 0' }}><b>Tên:</b> {contactEdit.name}</p>
							<p style={{ margin: '3px 0' }}><b>Số điện thoại:</b> {contactEdit.phone}</p>
							{contactEdit.address && (
								<p style={{ margin: '3px 0' }}><b>Địa chỉ:</b> {contactEdit.address}</p>
							)}
						</div>
						{contactEdit.status == 1 && (
							<div style={{
								background: '#fff7e6', border: '1px solid #ffd591',
								borderRadius: 4, padding: '6px 12px', marginBottom: 12,
							}}>
								<span style={{ color: '#fa8c16', fontSize: 13 }}>
									⚠️ Đã được gọi trước đó. Xác nhận sẽ cập nhật lại thời gian gọi.
								</span>
							</div>
						)}
						<div className="form-group" style={{ marginBottom: 16 }}>
							<label style={{ fontWeight: 500, display: 'block', marginBottom: 6 }}>
								Ghi chú cuộc gọi
							</label>
							<textarea
								style={{
									width: '100%', padding: '8px 10px',
									border: '1px solid #d9d9d9', borderRadius: 4,
									fontSize: 13, resize: 'vertical', minHeight: 100,
									boxSizing: 'border-box',
								}}
								value={callNote}
								onChange={e => setCallNote(e.target.value)}
								placeholder="Kết quả cuộc gọi, ghi chú thêm..."
							/>
						</div>
						<div className="d-flex justify-content-end gap">
							<Button white leftIcon={Icon.close} onClick={closePanel}>Đóng</Button>
							<Button primary leftIcon={Icon.success} onClick={handleConfirmCall}>
								Xác nhận đã gọi
							</Button>
						</div>
					</div>
				)}
			</Drawer>

			{/* Drawer: Tạo cuộc hẹn */}
			<Drawer
				title={`Tạo cuộc hẹn — ${contactEdit?.name || ''}`}
				open={activePanel === 'event'}
				onClose={closePanel}
				width={520}
				destroyOnClose
			>
				<SaleCallSourceEventForm
					contact={contactEdit}
					onHandleSubmit={handleCreateEvent}
				/>
			</Drawer>
		</div>
	);
}

export default SaleCallSourceDetail;

